/**
 * Conservative CSV-based matcher for FAA aircraft types.
 * Only suggests matches that appear in verified training data with no ambiguity.
 *
 * Approach:
 * 1. Normalize FAA manufacturer + model
 * 2. Look up in pre-built index of verified mappings
 * 3. Only suggest if exact match exists and is unambiguous
 * 4. Confidence always high (from verified data)
 */

// Minimum examples before we auto-trust a pattern
const MIN_SAFE_COUNT = 1;

export interface CsvMatchRow {
  faa_manufacturer: string;
  faa_model: string;
  airplx_make: string;
  airplx_model: string;
}

export interface CsvMatchSuggestion {
  airplx_make: string;
  airplx_model: string;
  confidence: number; // 0-1
  auto: boolean; // true means "safe to auto-apply"
}

type MatchKey = string; // `${make_norm}||${model_norm}`

/**
 * Normalize manufacturer name for matching.
 * Aggressive normalization to catch corporate name variations.
 */
function normalizeMake(raw: string): string {
  let normalized = (raw || "").toUpperCase().trim();

  // Strip common corporate suffixes
  const suffixes = [
    "AIRCRAFT COMPANY",
    "AIRCRAFT CO",
    "AIRCRAFT INC",
    "AIRCRAFT",
    "CORPORATION",
    "CORP",
    "COMPANY",
    "CO",
    "INC",
    "LLC",
    "LTD",
    "LIMITED",
    "INDUSTRIES",
    "AVIATION",
    "HELICOPTER",
    "HELICOPTERS",
    "FLUGZEUGWERKE",
    "SPA",
    "AG",
    "S A S",
  ];

  for (const suffix of suffixes) {
    // Try with space prefix
    const withSpace = new RegExp(`\\s+${suffix}$`, "i");
    normalized = normalized.replace(withSpace, "");
    // Try without space (handles "CESSNAAIRCRAFT")
    const withoutSpace = new RegExp(`${suffix}$`, "i");
    normalized = normalized.replace(withoutSpace, "");
  }

  // Normalize all punctuation to spaces
  normalized = normalized
    .replace(/[,.\/\-&()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized;
}

/**
 * Normalize model string for matching.
 * Very aggressive - strips ALL punctuation and whitespace.
 * "PA-28-140" → "PA28140", "172-M" → "172M", "King Air B350" → "KINGAIRB350"
 */
function normalizeModel(raw: string): string {
  let normalized = (raw || "").toUpperCase().trim();

  // Remove parenthetical content: "172M (Special)" → "172M"
  normalized = normalized.replace(/\([^)]*\)/g, "");

  // Strip ALL punctuation and whitespace - collapse to letters+numbers only
  // This ensures "172-M", "172 M", "172M" all become "172M"
  normalized = normalized.replace(/[-,./\s]+/g, "");

  return normalized;
}

export class CsvMatcher {
  private safeMap: Map<MatchKey, CsvMatchSuggestion> = new Map();

  stats: {
    totalRows: number;
    uniquePairs: number;
    safePairs: number;
  };

  private constructor(
    safeMap: Map<MatchKey, CsvMatchSuggestion>,
    stats: { totalRows: number; uniquePairs: number; safePairs: number }
  ) {
    this.safeMap = safeMap;
    this.stats = stats;
  }

  /**
   * Build matcher from array of CSV rows (training data)
   */
  static fromRows(rows: CsvMatchRow[]): CsvMatcher {
    const canonRows = rows.map((r) => ({
      make_norm: normalizeMake(r.faa_manufacturer),
      model_norm: normalizeModel(r.faa_model),
      airplx_make: r.airplx_make,
      airplx_model: r.airplx_model,
    }));

    const totalRows = canonRows.length;

    // Count by (make_norm, model_norm, airplx_make, airplx_model)
    const comboCounts = new Map<string, number>();
    canonRows.forEach((r) => {
      const key = `${r.make_norm}||${r.model_norm}||${r.airplx_make}||${r.airplx_model}`;
      comboCounts.set(key, (comboCounts.get(key) ?? 0) + 1);
    });

    // Aggregate per (make_norm, model_norm)
    type AggInfo = {
      totalExamples: number;
      targets: Set<string>; // "airplx_make||airplx_model"
    };

    const perPair = new Map<MatchKey, AggInfo>();

    comboCounts.forEach((count, comboKey) => {
      const [make_norm, model_norm, airplx_make, airplx_model] =
        comboKey.split("||");
      const pairKey: MatchKey = `${make_norm}||${model_norm}`;
      const existing = perPair.get(pairKey) ?? {
        totalExamples: 0,
        targets: new Set<string>(),
      };
      existing.totalExamples += count;
      existing.targets.add(`${airplx_make}||${airplx_model}`);
      perPair.set(pairKey, existing);
    });

    const uniquePairs = perPair.size;

    // Build safeMap: only pairs that:
    // - map to exactly one AirPlx (no ambiguity)
    // - have at least MIN_SAFE_COUNT examples
    const safeMap: Map<MatchKey, CsvMatchSuggestion> = new Map();

    perPair.forEach((info, pairKey) => {
      if (info.targets.size !== 1) return; // Ambiguous - skip
      if (info.totalExamples < MIN_SAFE_COUNT) return; // Not enough examples - skip

      const [target] = Array.from(info.targets);
      const [airplx_make, airplx_model] = target.split("||");
      safeMap.set(pairKey, {
        airplx_make,
        airplx_model,
        confidence: 0.999,
        auto: true,
      });
    });

    const stats = {
      totalRows,
      uniquePairs,
      safePairs: safeMap.size,
    };

    return new CsvMatcher(safeMap, stats);
  }

  /**
   * Suggest a match for given FAA manufacturer/model
   * Returns null if no safe match found in CSV
   */
  suggestMatch(
    faa_make: string,
    faa_model: string
  ): CsvMatchSuggestion | null {
    const make_norm = normalizeMake(faa_make);
    const model_norm = normalizeModel(faa_model);
    const key: MatchKey = `${make_norm}||${model_norm}`;
    return this.safeMap.get(key) ?? null;
  }
}
